import React, { useState, useMemo, useCallback } from "react";
import { Tooltip } from "antd";
import { FiFlag } from "react-icons/fi";

import dropDownUpArrow from "../../assets/icons/dropDownUpArrow.png";
import dropDownDownArrow from "../../assets/icons/dropDownDownArrow.png";

import DataTable from "../../components/Table/Table";
import { getDownloadMetrics } from "../../services/thunks/downloadMetricsThunks";

// ─── Time filter options ───────────────────────────────────────────────────────
const TIME_OPTIONS = [
  { label: "1 hour", value: "last1Hour" },
  { label: "3 hours", value: "last3Hours" },
  { label: "6 hours", value: "last6Hours" },
  { label: "12 hours", value: "last12Hours" },
  { label: "Daily", value: "daily" },
  { label: "Weekly", value: "weekly" },
  { label: "Monthly", value: "monthly" },
];

// ─── Helpers ───────────────────────────────────────────────────────────────────
// ─── Helpers ───────────────────────────────────────────────────────────────────

const renderCell = (text, record) => (
  <div className="min-h-[32px] flex items-center text-sm font-[400] font-['Public_Sans'] text-[#E2E2E2]">
    {text ?? "No User ID"}
  </div>
);

const renderWithTooltip = (text, record) => {
  if (!text) return renderCell("No User ID", record);
  return (
    <Tooltip title={text} placement="topLeft">
      <div className="min-h-[32px] flex items-center text-sm font-[400] font-['Public_Sans'] max-w-[260px] text-[#E2E2E2]">
        <span className="truncate block w-full">{text}</span>
      </div>
    </Tooltip>
  );
};

// ─── Component ─────────────────────────────────────────────────────────────────
const DownloadTracking = () => {
  const [selectedSort, setSelectedSort] = useState("last3Hours");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // The label shown in the dropdown button
  const selectedLabel =
    TIME_OPTIONS.find((o) => o.value === selectedSort)?.label ?? "Select Time";

  // Handle time filter selection
  const handleSelectSort = (value) => {
    setSelectedSort(value);
    setDropdownOpen(false);
    setRefreshTrigger((t) => t + 1);
  };

  // Table columns
  const tableColumns = useMemo(
    () => [
      {
        title: <div style={{ paddingLeft: "2.375rem" }}>User ID</div>,
        dataIndex: "userId",
        key: "userId",
        width: "20%",
        render: (text, record) => {
          const isFlagged = record?.isDailyLimitReached || record?.isAbuseFlagged;

          return (
            <div className="min-h-[32px] flex items-center gap-2 text-sm font-[400] font-['Public_Sans'] pl-[2.375rem] text-[#E2E2E2]">
              <span>{text ?? "No User ID"}</span>
              {isFlagged && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <FiFlag 
                    style={{ color: '#FF0000', fill: '#FF0000', flexShrink: 0 }} 
                    size={16} 
                  />
                </div>
              )}
            </div>
          );
        },
      },
      {
        title: "User Name",
        key: "userName",
        width: "20%",
        render: (_, record) =>
          renderWithTooltip(
            `${record.firstName ?? ""} ${record.lastName ?? ""}`.trim() || "No User Name",
            record,
          ),
      },
      {
        title: "Email",
        dataIndex: "email",
        key: "email",
        width: "35%",
        render: (text, record) => renderWithTooltip(text, record),
      },
      {
        title: "Downloads Count",
        key: "downloadsCount",
        width: "25%",
        render: (_, record) => (
          <div className="min-h-[32px] flex items-center text-sm font-[400] font-['Public_Sans'] text-[#E2E2E2]">
            {record[selectedSort] ?? 0}
          </div>
        ),
      },
    ],
    [selectedSort],
  );

  // Pass token + sort value down to thunk via customQueryParams
  const customQueryParams = useCallback(
    (params) => ({
      ...params,
      sort: selectedSort,
    }),
    [selectedSort],
  );

  return (
    <div className="px-6 py-10 text-white min-h-screen">
      {/* ── Filter row ─────────────────────────────────────────────────── */}
      <div className="flex justify-end mb-4">
        <div className="relative">
          {/* Dropdown trigger — same style as shared Dropdown component */}
          <button
            onClick={() => setDropdownOpen((o) => !o)}
            className="relative w-full px-4 py-2 pr-10 border border-[#808080] rounded-lg bg-[#45444433] text-[#E2E2E2] flex items-center min-w-[200px] h-[45px] font-['Public_Sans'] text-sm"
          >
            <span className="truncate text-left w-full font-medium">
              {selectedLabel}
            </span>
            <img
              src={dropdownOpen ? dropDownDownArrow : dropDownUpArrow}
              alt="arrow"
              className="absolute right-3 top-1/2 -translate-y-1/2 w-[12px] h-[8px] pointer-events-none"
            />
          </button>

          {/* Dropdown menu — same style as shared Dropdown component */}
          {dropdownOpen && (
            <div className="absolute right-0 w-full z-50 rounded-b-lg bg-[#111111D6] border border-[#505050]">
              {TIME_OPTIONS.map((option) => (
                <div
                  key={option.value}
                  onClick={() => handleSelectSort(option.value)}
                  className={`px-4 py-2 cursor-pointer border border-[#808080] font-['Public_Sans'] text-sm ${
                    selectedSort === option.value
                      ? "bg-[#565656] text-white font-semibold"
                      : "hover:bg-[#080B12]"
                  }`}
                >
                  <span className="truncate text-[#E3E3E3]">
                    {option.label}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Data Table ─────────────────────────────────────────────────── */}
      <DataTable
        columns={tableColumns}
        fetchData={getDownloadMetrics}
        refreshTrigger={refreshTrigger}
        headingBgColor="#0D141B"
        headingTextColor="#FFFFFF"
        customQueryParams={customQueryParams}
        initialPageSize={10}
        enableUrlParams={true}
      />
    </div>
  );
};

export default DownloadTracking;
